import React, { useMemo } from 'react';
import { StyleSheet } from 'react-native';

import { Box, Row } from '@/components/lib/components';
import { MaxWidthWrapper } from '@/feature/responsive-design/WebComponents';
import { useResponsiveColumnCount } from '@/feature/responsive-design/hooks/use-responsive-column-count';
import { SocialFeedEntry } from '@/types/socialFeed.generated';

import { SocialFeedCard } from '../SocialFeedCard';

/**
 * Lays out SocialFeedCards in a row. On large (web) viewports it renders multiple
 * columns per row, while on mobile it stays a single column.
 *
 * Since FlashList has no prop to control the number of elements per row, we build the
 * row manually: at each step we collect the current item and the next ones (up to
 * columnsCount) and render them together. Items already rendered in a previous step are
 * skipped so we don't render duplicates.
 */
export const SocialFeedCardsRow = ({
    index,
    items,
    disableMaxWidth = false,
}: {
    index: number;
    items: SocialFeedEntry[];
    disableMaxWidth?: boolean;
}) => {
    const columnsCount = useResponsiveColumnCount([1, 1, 1, 2, 3]);

    const cards = useMemo(() => {
        const list = [];
        let currentIndex = index;
        while (list.length < columnsCount && currentIndex < items.length) {
            const item = items[currentIndex];
            list.push(
                <Box key={`${currentIndex}-${item.createdDate}`} flex={1}>
                    <SocialFeedCard feedEntry={item} />
                </Box>
            );
            currentIndex++;
        }
        return list;
    }, [index, columnsCount, items]);

    // This element has already been added in a previous step. Skip to avoid duplicates.
    if (index % columnsCount !== 0) {
        return null;
    }

    if (disableMaxWidth) {
        return <Row style={styles.row}>{cards}</Row>;
    }

    return (
        <MaxWidthWrapper>
            <Row style={styles.row}>{cards}</Row>
        </MaxWidthWrapper>
    );
};

const styles = StyleSheet.create({
    row: {
        paddingHorizontal: 16,
        gap: 16,
    },
});
